import { useState } from 'react'

import isEqual from 'lodash/isEqual'
import moment from 'moment'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'
import {
  DEFAULT_ASSIGNMENT_COST_CENTER_ID,
  DEFAULT_ASSIGNMENT_PERCENTAGE,
  defaultAssignment,
  defaultStructuredAssignment,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/defaults'
import { formatAssignments } from 'modules/cost_centers/components/CostCenterAssigments/lib/formatter'
import {
  CostCenterAssignment,
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import { useAssignmentsValidator } from './useAssignmentsValidator'
import { DateRange } from './validations/overlaps'

// ValueAssignment type used by the legacy CostCenterPicker (DimensionMultipleValuePicker)
interface ValueAssignment {
  name: string
  id: string
  percentage: number
}

interface Props {
  currentAssignments: (FormattedAssignmentWithDeletion | FormattedAssignment)[]
  allAssignments: CostCenterAssignment[]
  defaultDateRange?: { startDate: string; endDate: string }
  hasNewStructureEnabled?: boolean
  isCreating?: boolean
}

/**
 * Get current (active) assignments - those without an end date
 */
const getCurrentAssignments = (
  allAssignments: CostCenterAssignment[],
  hasNewStructureEnabled: boolean
): FormattedAssignment[] => {
  const currentAssignments = allAssignments.filter((assignment) => !assignment.endDate)
  return formatAssignments(currentAssignments, hasNewStructureEnabled)
}

/**
 * Get the most recent past assignments grouped by start date.
 * Returns all assignments that share the latest start date among ended assignments.
 */
const getImmediatePastAssignments = (
  allAssignments: CostCenterAssignment[],
  hasNewStructureEnabled: boolean
): FormattedAssignment[] => {
  const pastAssignments = allAssignments.filter((assignment) => assignment.endDate)
  const firstPastAssignment = pastAssignments[0]
  if (!firstPastAssignment) return []

  const firstStartDate = firstPastAssignment.startDate
  const latestStartDate = pastAssignments.reduce((latest, assignment) => {
    return moment(assignment.startDate).isAfter(moment(latest)) ? assignment.startDate : latest
  }, firstStartDate)

  const mostRecentGroup = pastAssignments.filter((assignment) =>
    moment(assignment.startDate).isSame(moment(latestStartDate), 'day')
  )

  return formatAssignments(mostRecentGroup, hasNewStructureEnabled)
}

/**
 * Get future assignments — those with a start date strictly after today.
 * Returns all assignments that share the earliest future start date.
 */
const getFutureAssignments = (
  allAssignments: CostCenterAssignment[],
  hasNewStructureEnabled: boolean
): FormattedAssignment[] => {
  const today = moment().startOf('day')
  const futureAssignments = allAssignments.filter((assignment) =>
    moment(assignment.startDate).isAfter(today)
  )
  const firstFutureAssignment = futureAssignments[0]
  if (!firstFutureAssignment) return []

  const firstStartDate = firstFutureAssignment.startDate
  const earliestStartDate = futureAssignments.reduce((earliest, assignment) => {
    return moment(assignment.startDate).isBefore(moment(earliest)) ? assignment.startDate : earliest
  }, firstStartDate)

  const earliestGroup = futureAssignments.filter((assignment) =>
    moment(assignment.startDate).isSame(moment(earliestStartDate), 'day')
  )

  return formatAssignments(earliestGroup, hasNewStructureEnabled)
}

/**
 * In structured mode during creation, get assignments for prepopulation:
 * 1. Current assignments (no end date) if they exist
 * 2. Otherwise, the most recent past assignment group (by start date)
 * 3. Otherwise, the earliest future assignment group (by start date)
 */
const getAssignmentsForPrepopulation = (
  allAssignments: CostCenterAssignment[],
  hasNewStructureEnabled: boolean
): FormattedAssignment[] => {
  const current = getCurrentAssignments(allAssignments, hasNewStructureEnabled)
  if (current.length > 0) return current

  const past = getImmediatePastAssignments(allAssignments, hasNewStructureEnabled)
  if (past.length > 0) return past

  return getFutureAssignments(allAssignments, hasNewStructureEnabled)
}

/**
 * Calculate end date for current assignments (one day before the new start date)
 */
const calculateEndDateForCurrentAssignments = (newStartDate: string): string => {
  return moment(newStartDate).subtract(1, 'day').format('YYYY-MM-DD')
}

export const useAssignmentsManager = ({
  currentAssignments,
  allAssignments,
  defaultDateRange,
  hasNewStructureEnabled = false,
  isCreating = false,
}: Props) => {
  // For new structure enabled in creation mode, prepopulate with current or immediate past assignments
  const shouldPrepopulateAssignments = hasNewStructureEnabled && isCreating
  const assignmentsForPrepopulation = shouldPrepopulateAssignments
    ? getAssignmentsForPrepopulation(allAssignments, hasNewStructureEnabled)
    : []

  // Determine the initial start date for the new assignment:
  // - When prepopulating from future assignments, start the day after the future group's start date
  // - Otherwise, default to today
  const initialStartDate = (() => {
    if (!hasNewStructureEnabled || !isCreating) return ''

    if (assignmentsForPrepopulation.length > 0) {
      // Check if the prepopulation source is future assignments (startDate > today)
      const firstPrepopAssignment = assignmentsForPrepopulation[0]
      if (firstPrepopAssignment) {
        const today = moment().startOf('day')
        const firstPrepopStartDate = firstPrepopAssignment.startDate
        if (moment(firstPrepopStartDate).isAfter(today)) {
          // Future assignments: new assignment starts the day after their end date,
          // or if they have no end date, the day after their start date
          const futureEndDate = firstPrepopAssignment.endDate
          const referenceDate = futureEndDate || firstPrepopStartDate
          return moment(referenceDate).add(1, 'day').format('YYYY-MM-DD')
        }
      }
    }

    return moment().format('YYYY-MM-DD')
  })()

  // Mark prepopulated assignments as default so their cost center selectors are disabled
  // Also update their dates to match the new assignment period
  const prepopulatedAssignments: FormattedAssignment[] = assignmentsForPrepopulation.map(
    (assignment) => ({
      ...assignment,
      isDefault: true,
      startDate: initialStartDate, // Set to the new start date, not their original date
      endDate: '', // New assignments have no end date (ongoing)
    })
  )

  //------------ State Management ------------

  const [assignmentToCreateTemplate, setAssignmentToCreateTemplate] = useState<
    FormattedAssignment[]
  >([])
  // When prepopulating, the form is already filled and ready to submit,
  // so we consider the user as having interacted from the start.
  const [userInteracted, setUserInteracted] = useState(
    shouldPrepopulateAssignments && assignmentsForPrepopulation.length > 0
  )
  const [assignmentsToCreate, setAssignmentsToCreate] = useState<FormattedAssignment[]>(
    shouldPrepopulateAssignments ? prepopulatedAssignments : []
  )
  const [assignmentsToUpdate, setAssignmentsToUpdate] =
    useState<FormattedAssignmentWithDeletion[]>(currentAssignments)
  const [assignments, setAssignments] = useState<FormattedAssignment[]>(
    shouldPrepopulateAssignments
      ? [...prepopulatedAssignments]
      : [...assignmentsToCreate, ...assignmentsToUpdate]
  )

  // Track the current assignments without end date for updating when start date changes
  const [currentAssignmentsToEnd, setCurrentAssignmentsToEnd] = useState<
    FormattedAssignmentWithDeletion[]
  >([])

  //------------ Date Range State ------------

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: initialStartDate || currentAssignments[0]?.startDate || '',
    endDate: currentAssignments[0]?.endDate || defaultDateRange?.endDate || '',
  })

  //------------ Initialize Current Assignments for New Structure ------------

  useUnsafeEffect(() => {
    if (shouldPrepopulateAssignments && currentAssignmentsToEnd.length === 0) {
      // In structured mode, current assignments always have an end date when terminated.
      // Pick current (no end date) assignments to end, or if none exist, pick the
      // immediate past group so the system can update their end dates.
      const activeRawAssignments = allAssignments.filter((a) => !a.endDate)
      const rawAssignmentsToEnd =
        activeRawAssignments.length > 0
          ? activeRawAssignments
          : (() => {
              const pastAssignments = allAssignments.filter((a) => a.endDate)
              const firstPast = pastAssignments[0]
              if (!firstPast) return []
              const firstStartDate = firstPast.startDate
              const latestStartDate = pastAssignments.reduce((latest, a) => {
                return moment(a.startDate).isAfter(moment(latest)) ? a.startDate : latest
              }, firstStartDate)
              return pastAssignments.filter((a) =>
                moment(a.startDate).isSame(moment(latestStartDate), 'day')
              )
            })()

      if (rawAssignmentsToEnd.length > 0) {
        const formattedForUpdate = formatAssignments(
          rawAssignmentsToEnd,
          hasNewStructureEnabled
        ).map((a) => ({
          ...a,
          markedForDeletion: false,
        }))
        setCurrentAssignmentsToEnd(formattedForUpdate)
      }
    }
  }, [
    shouldPrepopulateAssignments,
    allAssignments,
    hasNewStructureEnabled,
    currentAssignmentsToEnd.length,
  ])

  //------------ Track User Interaction ------------

  useUnsafeEffect(() => {
    if (userInteracted) return

    // For structured mode edits (not creating), detect changes by comparing
    // current assignments against the original ones. This is needed because
    // updateStructuredField updates both `assignments` and `assignmentsToUpdate`
    // simultaneously, so the generic comparison below never detects a difference.
    if (hasNewStructureEnabled && !isCreating && currentAssignments.length > 0) {
      const hasPercentageOrCostCenterChanged = currentAssignments.some((original) => {
        const current = assignments.find((a) => a.id === original.id)
        if (!current) return true // assignment was removed
        return (
          current.percentage !== original.percentage ||
          current.costCenterId !== original.costCenterId
        )
      })
      const hasNewAssignments = assignments.some(
        (a) => !currentAssignments.find((orig) => orig.id === a.id)
      )
      if (hasPercentageOrCostCenterChanged || hasNewAssignments) {
        setUserInteracted(true)
        return
      }
    }

    const assignmentsAreDifferent = !isEqual(assignments, [
      ...assignmentsToCreate,
      ...assignmentsToUpdate,
    ])

    if (assignmentsAreDifferent) {
      setUserInteracted(true)
    }
  }, [
    assignments,
    userInteracted,
    assignmentsToCreate,
    assignmentsToUpdate,
    hasNewStructureEnabled,
    isCreating,
    currentAssignments,
  ])

  //------------ Validations ------------

  const { validForSave, validations } = useAssignmentsValidator({
    selectedDateRange,
    currentAssignments: currentAssignments,
    displayedAssignments: assignments,
    allAssignments,
    userInteracted: userInteracted,
    hasNewStructureEnabled,
    isCreating,
    assignmentIdsBeingUpdated: currentAssignmentsToEnd.map((a) => a.id),
  })

  //------------ Date Range Management ------------

  const setNewDateRange = ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    const dateToAssign = {
      ...(startDate ? { startDate } : {}),
      ...(typeof endDate === 'string' ? { endDate } : {}),
    }

    setSelectedDateRange((prevDateRange) => ({
      ...prevDateRange,
      ...dateToAssign,
    }))

    setAssignmentToCreateTemplate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    setAssignmentsToCreate((prevAssignments) => {
      return prevAssignments.map((assignment) => ({
        ...assignment,
        ...dateToAssign,
      }))
    })

    // When in creation mode with new structure enabled, update the current assignments
    // to have end date set to one day before the new start date.
    // Only update assignments that don't already have an end date — past assignments
    // with an existing end date should keep their original termination date.
    if (shouldPrepopulateAssignments && startDate && currentAssignmentsToEnd.length > 0) {
      const endDateForCurrentAssignments = calculateEndDateForCurrentAssignments(startDate)
      const updatedCurrentAssignments = currentAssignmentsToEnd.map((assignment) => ({
        ...assignment,
        endDate: assignment.endDate || endDateForCurrentAssignments,
      }))
      setAssignmentsToUpdate(updatedCurrentAssignments)
    } else {
      setAssignmentsToUpdate((prevAssignments) => {
        return prevAssignments.map((assignment) => ({
          ...assignment,
          ...dateToAssign,
        }))
      })
    }
  }

  //==========================================================================
  // Legacy Mode Functions
  //==========================================================================

  const assignmentsToUpdateNotMarkedForDeletion = assignmentsToUpdate.filter(
    (v) => !v.markedForDeletion
  )

  //------------ Legacy: Add New Assignment ------------

  const addNewAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
      previousCostCenterId:
        assignments
          .map((item) => item.costCenterId)
          .filter((id): id is string => !!id)
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))[0] ??
        DEFAULT_ASSIGNMENT_COST_CENTER_ID,
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Legacy: Delete Assignment ------------

  const deleteValue = (value: ValueAssignment) => {
    const storedAssignment = assignmentsToUpdate.find((v) => String(v.costCenterId) === value.id)
    const assignmentInCreate = assignmentsToCreate.find((v) => String(v.costCenterId) === value.id)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate(assignmentsToCreate.filter((v) => v.costCenterId !== value.id))
    }

    setAssignments(assignments.filter((v) => String(v.costCenterId) !== value.id))
  }

  //------------ Legacy: Template Management Helper ------------

  const manageCostcenterInAssignmentToCreateInTemplate = (
    value: FormattedAssignment,
    newValue: ValueAssignment
  ) => {
    const { costCenterId } = value

    if (
      newValue.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE ||
      Number(newValue.id) <= Number(DEFAULT_ASSIGNMENT_COST_CENTER_ID)
    ) {
      setAssignmentToCreateTemplate([
        ...assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId),
        { ...value, ...newValue, costCenterId: newValue.id },
      ])
    } else {
      setAssignmentToCreateTemplate(
        assignmentToCreateTemplate.filter((v) => v.costCenterId !== costCenterId)
      )

      setAssignmentsToCreate([
        ...assignmentsToCreate,
        { ...value, ...newValue, costCenterId: newValue.id },
      ])
    }
  }

  //------------ Legacy: Update Assignment ------------

  const updateAssignment = (valueId: string, newValue: ValueAssignment) => {
    const assignment = assignments.find((v) => v.costCenterId == valueId)

    if (!assignment) return

    const costCenterInUpdateAssignments = assignmentsToUpdateNotMarkedForDeletion.find(
      (v) => v.costCenterId == valueId
    )

    const costCenterInAssignmentToCreateInTemplate = assignmentToCreateTemplate.find(
      (v) => v.costCenterId == valueId
    )

    if (costCenterInUpdateAssignments) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== costCenterInUpdateAssignments.id),
        {
          ...costCenterInUpdateAssignments,
          percentage: newValue.percentage,
          costCenterId: newValue.id,
        },
      ])
    } else if (costCenterInAssignmentToCreateInTemplate) {
      manageCostcenterInAssignmentToCreateInTemplate(
        costCenterInAssignmentToCreateInTemplate,
        newValue
      )
    } else {
      const assignmentToPatch = assignmentsToCreate.find((v) => v.costCenterId == valueId)

      if (!assignmentToPatch) return

      setAssignmentsToCreate([
        ...assignmentsToCreate.filter((v) => v.costCenterId !== newValue.id),
        { ...assignmentToPatch, ...newValue, costCenterId: newValue.id },
      ])
    }

    // In order to not to update the order of the assignments, we need to map the assignments and
    // update the assignment with the new value
    setAssignments(
      assignments.map((item) =>
        String(item.costCenterId) === valueId
          ? {
              ...item,
              ...newValue,
              costCenterId: newValue.id,
            }
          : item
      )
    )
  }

  //==========================================================================
  // Structured Mode Functions
  //==========================================================================

  //------------ Structured: Add New Assignment ------------

  const addNewStructuredAssignment = useUnsafeCallback(() => {
    const newAssignment = defaultStructuredAssignment({
      startDate: String(selectedDateRange.startDate),
      endDate: selectedDateRange.endDate,
    })

    setAssignmentToCreateTemplate((prevAssignments) => [...prevAssignments, newAssignment])
    setAssignments([...assignments, newAssignment])

    track(trackingEvents.accounting.addNewCostCenterToAssignment, {
      countAdded:
        assignments.filter((item) => item.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE).length + 1,
    })
  }, [assignments, selectedDateRange, setAssignmentToCreateTemplate, setAssignments])

  //------------ Structured: Delete Assignment ------------

  const deleteStructuredAssignment = (assignmentId: string) => {
    const storedAssignment = assignmentsToUpdate.find((v) => v.id === assignmentId)
    const assignmentInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    const assignmentInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)

    if (storedAssignment) {
      setAssignmentsToUpdate([
        ...assignmentsToUpdate.filter((v) => v.id !== storedAssignment.id),
        { ...storedAssignment, markedForDeletion: true },
      ])
    }

    if (assignmentInCreate) {
      setAssignmentsToCreate(assignmentsToCreate.filter((v) => v.id !== assignmentId))
    }

    if (assignmentInTemplate) {
      setAssignmentToCreateTemplate(assignmentToCreateTemplate.filter((v) => v.id !== assignmentId))
    }

    setAssignments(assignments.filter((v) => v.id !== assignmentId))
  }

  //------------ Structured: Generic Field Updater ------------

  const updateStructuredField = (assignmentId: string, updates: Partial<FormattedAssignment>) => {
    const currentAssignment = assignments.find((v) => v.id === assignmentId)
    if (!currentAssignment) return

    const updatedAssignment = { ...currentAssignment, ...updates }

    const existingInUpdate = assignmentsToUpdate.find((v) => v.id === assignmentId)
    const isCurrentAssignmentBeingEnded =
      shouldPrepopulateAssignments && currentAssignmentsToEnd.some((v) => v.id === assignmentId)
    if (existingInUpdate && !isCurrentAssignmentBeingEnded) {
      setAssignmentsToUpdate(
        assignmentsToUpdate.map((v) => (v.id === assignmentId ? { ...v, ...updates } : v))
      )
    }

    // Check if this is in the template (new assignment not yet valid)
    const existingInTemplate = assignmentToCreateTemplate.find((v) => v.id === assignmentId)
    if (existingInTemplate) {
      const updatedTemplateAssignment = { ...existingInTemplate, ...updates }

      // Check if assignment is now valid (has a cost center and valid percentage)
      const hasValidCostCenter = updatedTemplateAssignment.costCenterId != null
      const hasValidPercentage =
        updatedTemplateAssignment.percentage !== DEFAULT_ASSIGNMENT_PERCENTAGE &&
        updatedTemplateAssignment.percentage > 0

      if (hasValidCostCenter && hasValidPercentage) {
        // Move from template to create list
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.filter((v) => v.id !== assignmentId)
        )
        setAssignmentsToCreate([...assignmentsToCreate, updatedTemplateAssignment])
      } else {
        // Keep in template
        setAssignmentToCreateTemplate(
          assignmentToCreateTemplate.map((v) =>
            v.id === assignmentId ? updatedTemplateAssignment : v
          )
        )
      }
    }

    // Check if this is in the create list
    const existingInCreate = assignmentsToCreate.find((v) => v.id === assignmentId)
    if (existingInCreate) {
      setAssignmentsToCreate(
        assignmentsToCreate.map((v) => (v.id === assignmentId ? { ...v, ...updates } : v))
      )
    }

    // Update the display list
    setAssignments(assignments.map((item) => (item.id === assignmentId ? updatedAssignment : item)))
  }

  //------------ Structured: Update Cost Center ------------

  const updateCostCenter = (assignmentId: string, option: CostCenterOption | null) => {
    // If selecting a new cost center, remove it from any other assignments (deduplication)
    if (option) {
      const otherAssignments = assignments.filter((a) => a.id !== assignmentId)
      otherAssignments.forEach((otherAssignment) => {
        if (otherAssignment.costCenterId === option.id) {
          // Remove the cost center from the other assignment
          updateStructuredField(otherAssignment.id, {
            costCenterId: undefined,
            name: '',
          })
        }
      })
    }

    updateStructuredField(assignmentId, {
      costCenterId: option ? option.id : undefined,
      name: option?.name ?? '',
    })
  }

  //------------ Structured: Update Percentage ------------

  const updateStructuredPercentage = (assignmentId: string, percentage: number) => {
    updateStructuredField(assignmentId, { percentage })
  }

  //==========================================================================
  // Return
  //==========================================================================

  return {
    assignmentsToCreate,
    assignmentsToUpdate,
    selectedDateRange,
    assignments,
    validForSave,
    validations,
    // Legacy mode functions
    addNewAssignment,
    deleteValue,
    updateAssignment,
    // Structured mode functions
    addNewStructuredAssignment,
    deleteStructuredAssignment,
    updateCostCenter,
    updateStructuredPercentage,
    // Shared functions
    setAssignmentsToCreate,
    setAssignmentsToUpdate,
    setNewDateRange,
  }
}
